import {DataStatus} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  AftersalesPaymentMethod,
  useGetCheckoutLinkForServiceOrderQuery,
  usePostCheckoutForServiceOrderMutation,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetServiceOrderQuery,
  useGetServiceOrderVariantPaymentTypeQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';
import {AftersalesCheckout} from '@dms/shared-aftersales';

import {isFetchBaseQueryError, RequiredTestIdProps} from 'shared/web';

import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';

interface CheckoutProps extends RequiredTestIdProps {}

export function Checkout(props: CheckoutProps) {
  const {orderId, serviceCaseId} = useWorkshopUrl();
  const [postCheckoutForServiceOrder, {isLoading: isPostCheckoutForServiceOrderLoading}] =
    usePostCheckoutForServiceOrderMutation();

  const {
    data: checkout,
    isLoading: isCheckoutLoading,
    isError: isCheckoutError,
    isFetching: isCheckoutFetching,
    error: checkoutError,
  } = useGetCheckoutLinkForServiceOrderQuery(
    {serviceOrderId: orderId},
    {refetchOnMountOrArgChange: true}
  );

  const {
    data: serviceOrder,
    isLoading: isServiceOrderLoading,
    isError: isServiceOrderError,
  } = useGetServiceOrderQuery({
    serviceCaseId,
    serviceOrderId: orderId,
  });

  const {
    data: orderVariantPaymentType,
    isLoading: isOrderVariantPaymentTypeLoading,
    isError: isOrderVariantPaymentTypeError,
  } = useGetServiceOrderVariantPaymentTypeQuery(
    {serviceOrderVariantId: serviceOrder?.serviceOrderVariantId ?? ''},
    {skip: isNilOrEmpty(serviceOrder?.serviceOrderVariantId)}
  );

  const orderVariantDefaultPaymentType = orderVariantPaymentType?.defaultPaymentType;

  const defaultPaymentType = match<
    typeof orderVariantDefaultPaymentType,
    AftersalesPaymentMethod | undefined
  >(orderVariantDefaultPaymentType)
    .with('CARD', 'CASH', 'BANK_TRANSFER', 'INTERNAL', (paymentType) => paymentType)
    .with('CASH_ON_DELIVERY', () => 'INTERNAL')
    .when(isNil, () => undefined)
    .exhaustive();

  const isCheckoutNotFound =
    isNotNil(checkoutError) && isFetchBaseQueryError(checkoutError) && checkoutError.status === 400;

  const isLoading =
    isCheckoutLoading ||
    isServiceOrderLoading ||
    isOrderVariantPaymentTypeLoading ||
    isCheckoutFetching;

  const isError =
    isServiceOrderError ||
    isOrderVariantPaymentTypeError ||
    (isCheckoutError && !isCheckoutNotFound);

  return (
    <DataStatus
      isLoading={isLoading}
      isError={isError}
      isEmpty={(!isCheckoutError && isNilOrEmpty(checkout)) || isCheckoutNotFound}
      emptyMessage={i18n.t('entity.checkout.labels.noCheckout')}
      action={{
        title: i18n.t('entity.checkout.labels.create'),
        onClick: () => {
          postCheckoutForServiceOrder({serviceOrderId: orderId}).unwrap().catch(handleApiError);
        },
        variant: 'link',
        leftIcon: 'content/add_circle',
        isLoading: isPostCheckoutForServiceOrderLoading,
      }}
    >
      <AftersalesCheckout
        checkoutId={checkout?.checkoutId ?? ''}
        defaultPaymentType={defaultPaymentType}
        recordId={orderId}
        resourceId={EntityResourceIds.serviceOrder}
        serviceCaseId={serviceCaseId}
        data-testid={props['data-testid']}
      />
    </DataStatus>
  );
}
